// ./src/components/generic/FilePreviewCard/FilePreviewCard.tsx
import React, { useState, useCallback, useMemo } from "react";
import {
  Card,
  Image,
  Text,
  Group,
  ActionIcon,
  Tooltip,
  Box,
  Center,
  useMantineTheme,
} from "@mantine/core";
import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";

import {
  formatFileSize,
  getFileIcon,
  isPreviewableImage,
  canOpenInBrowser,
  truncateFilename,
  downloadFile,
  openInNewTab,
} from "@/utils/fileHelpers";

import { UploadedFile } from "@/utils/api/UploadedFileService";

import { AppIcon } from "../AppIcon";
import { ImageViewer } from "../ImageViewer";

// ============================================
// Types
// ============================================

export interface FilePreviewCardProps {
  /** The file to display */
  file: UploadedFile;

  /** Callback when remove button is clicked */
  onRemove?: (fileId: string) => void;

  /** Callback when view/preview is triggered */
  onView?: (file: UploadedFile) => void;

  /** Whether to show the remove button */
  showRemove?: boolean;

  /** Card size variant */
  size?: "sm" | "md" | "lg";

  /** Whether the card is in a readonly state */
  readonly?: boolean;

  /** Whether the card is currently being dragged */
  isDragging?: boolean;

  /** Drag handle props from react-beautiful-dnd / @hello-pangea/dnd */
  dragHandleProps?: DraggableProvidedDragHandleProps | null;

  /** Whether to show the drag handle */
  showDragHandle?: boolean;
}

// ============================================
// Size Configurations
// ============================================

interface SizeConfig {
  cardWidth: number | string;
  thumbnailHeight: number;
  iconSize: number;
  fontSize: "xs" | "sm" | "md";
  padding: "xs" | "sm" | "md";
  actionSize: "xs" | "sm" | "md";
}

const SIZE_CONFIGS: Record<"sm" | "md" | "lg", SizeConfig> = {
  sm: {
    cardWidth: 140,
    thumbnailHeight: 80,
    iconSize: 28,
    fontSize: "xs",
    padding: "xs",
    actionSize: "xs",
  },
  md: {
    cardWidth: 180,
    thumbnailHeight: 100,
    iconSize: 36,
    fontSize: "sm",
    padding: "sm",
    actionSize: "sm",
  },
  lg: {
    cardWidth: 220,
    thumbnailHeight: 120,
    iconSize: 44,
    fontSize: "md",
    padding: "md",
    actionSize: "md",
  },
};

// ============================================
// Component
// ============================================

export const FilePreviewCard: React.FC<FilePreviewCardProps> = ({
  file,
  onRemove,
  onView,
  showRemove = true,
  size = "md",
  readonly = false,
  isDragging = false,
  dragHandleProps,
  showDragHandle = false,
}) => {
  const { t } = useTranslation(["common"]);
  const theme = useMantineTheme();

  // State for image viewer
  const [imageViewerOpened, setImageViewerOpened] = useState(false);

  // Get size configuration
  const config = SIZE_CONFIGS[size];

  // Determine file properties
  const isImage = useMemo(
    () => isPreviewableImage(file.mimeType),
    [file.mimeType]
  );
  const canOpen = useMemo(
    () => canOpenInBrowser(file.mimeType),
    [file.mimeType]
  );
  const fileIcon = useMemo(() => getFileIcon(file.mimeType), [file.mimeType]);

  // Get the image URL for preview (prefer thumbnail for cards)
  const previewUrl = file.thumbnailUrl || file.fileUrl;
  const fullImageUrl = file.fileUrl || file.thumbnailUrl;

  // Truncated filename for display
  const displayName = useMemo(() => {
    const name = file.name || file.originalFilename || "Unknown file";
    const maxLength = size === "sm" ? 15 : size === "md" ? 20 : 25;
    return truncateFilename(name, maxLength);
  }, [file.name, file.originalFilename, size]);

  // Full filename for tooltip
  const fullName = file.name || file.originalFilename || "Unknown file";

  // Formatted file size
  const formattedSize = useMemo(
    () => formatFileSize(file.sizeBytes),
    [file.sizeBytes]
  );

  // Handlers
  const handleView = useCallback(() => {
    if (isImage) {
      setImageViewerOpened(true);
    } else if (canOpen && fullImageUrl) {
      openInNewTab(fullImageUrl);
    }
    onView?.(file);
  }, [isImage, canOpen, fullImageUrl, file, onView]);

  const handleDownload = useCallback(() => {
    if (fullImageUrl) {
      downloadFile(fullImageUrl, fullName);
    }
  }, [fullImageUrl, fullName]);

  const handleRemove = useCallback(() => {
    if (file.id) {
      onRemove?.(file.id);
    }
  }, [file.id, onRemove]);

  const handleOpenInTab = useCallback(() => {
    if (fullImageUrl) {
      openInNewTab(fullImageUrl);
    }
  }, [fullImageUrl]);

  // Close image viewer
  const handleCloseImageViewer = useCallback(() => {
    setImageViewerOpened(false);
  }, []);

  // Card styles when dragging
  const cardStyles = useMemo((): React.CSSProperties => {
    if (isDragging) {
      return {
        boxShadow: theme.shadows.lg,
        transform: "rotate(2deg) scale(1.02)",
        border: `2px solid ${theme.colors.blue[4]}`,
      };
    }
    return {};
  }, [isDragging, theme]);

  return (
    <>
      <Card
        shadow="sm"
        padding={0}
        radius="md"
        withBorder
        w={config.cardWidth}
        style={{
          overflow: "hidden",
          transition: "box-shadow 0.2s ease, transform 0.2s ease",
          ...cardStyles,
        }}
      >
        {/* Drag handle header - only shown when reorderable */}
        {showDragHandle && dragHandleProps && (
          <Box
            {...dragHandleProps}
            bg="gray.1"
            py={6}
            style={{
              cursor: "grab",
              borderBottom: `1px solid ${theme.colors.gray[3]}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
            }}
          >
            <AppIcon
              iconName="GripHorizontal"
              size={16}
              style={{ color: theme.colors.gray[5] }}
            />
          </Box>
        )}

        {/* Thumbnail / Icon area */}
        <Card.Section>
          <Box
            h={config.thumbnailHeight}
            bg="gray.1"
            style={{
              position: "relative",
              cursor: isImage || canOpen ? "pointer" : "default",
            }}
            onClick={isImage || canOpen ? handleView : undefined}
          >
            {isImage && previewUrl ? (
              <Image
                src={previewUrl}
                alt={fullName}
                h={config.thumbnailHeight}
                fit="cover"
              />
            ) : (
              <Center h="100%">
                <AppIcon
                  iconName={fileIcon}
                  size={config.iconSize}
                  style={{ color: theme.colors.gray[5] }}
                />
              </Center>
            )}

            {/* Overlay for viewable files */}
            {(isImage || canOpen) && (
              <Box
                pos="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bg="rgba(0,0,0,0)"
                style={{
                  transition: "background-color 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                className="file-preview-overlay"
              >
                <AppIcon
                  iconName={isImage ? "Eye" : "ExternalLink"}
                  size={24}
                  style={{
                    color: "white",
                    opacity: 0,
                    transition: "opacity 0.2s ease",
                  }}
                  className="file-preview-icon"
                />
              </Box>
            )}
          </Box>
        </Card.Section>

        {/* File info and actions */}
        <Box p={config.padding}>
          {/* File info */}
          <Tooltip label={fullName} disabled={fullName === displayName}>
            <Text size={config.fontSize} fw={500} lineClamp={1}>
              {displayName}
            </Text>
          </Tooltip>
          <Text size="xs" c="dimmed">
            {formattedSize}
          </Text>

          {/* Actions */}
          {!readonly && (
            <Group gap={4} mt="xs" justify="flex-start">
              {/* View button - for images */}
              {isImage && (
                <Tooltip label={t("common:attachments.viewImage")}>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size={config.actionSize}
                    onClick={handleView}
                  >
                    <AppIcon iconName="Eye" size={14} />
                  </ActionIcon>
                </Tooltip>
              )}

              {/* Open in tab - for PDFs and other viewable files */}
              {canOpen && !isImage && (
                <Tooltip label={t("common:attachments.openInTab")}>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size={config.actionSize}
                    onClick={handleOpenInTab}
                  >
                    <AppIcon iconName="ExternalLink" size={14} />
                  </ActionIcon>
                </Tooltip>
              )}

              {/* Download button */}
              <Tooltip label={t("common:attachments.download")}>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size={config.actionSize}
                  onClick={handleDownload}
                  disabled={!fullImageUrl}
                >
                  <AppIcon iconName="Download" size={14} />
                </ActionIcon>
              </Tooltip>

              {/* Remove button */}
              {showRemove && onRemove && (
                <Tooltip label={t("common:attachments.remove")}>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size={config.actionSize}
                    onClick={handleRemove}
                  >
                    <AppIcon iconName="Trash" size={14} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          )}
        </Box>
      </Card>

      {/* Image viewer modal */}
      {isImage && fullImageUrl && (
        <ImageViewer
          opened={imageViewerOpened}
          onClose={handleCloseImageViewer}
          src={fullImageUrl}
          alt={fullName}
          fileName={fullName}
        />
      )}

      {/* Hover styles */}
      <style>{`
        .file-preview-overlay:hover {
          background-color: rgba(0, 0, 0, 0.4) !important;
        }
        .file-preview-overlay:hover .file-preview-icon {
          opacity: 1 !important;
        }
      `}</style>
    </>
  );
};
